import { useEffect, useMemo, useState } from "react"

import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { usePublicClasses } from "../hooks/usePublicClasses"
import { usePublicSessions } from "../hooks/usePublicSessions"
import type { PublicClassFilters } from "../types"
import { ClassCard } from "./ClassCard"
import { ClassFilters } from "./ClassFilters"

const DEFAULT_FILTERS: PublicClassFilters = {
  sort_by: "day_time",
  limit: 50,
}

const selectStyle = {
  padding: "8px 12px",
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "#E2E8F0",
  borderRadius: "6px",
  backgroundColor: "white",
  fontSize: "14px",
  cursor: "pointer",
  outline: "none",
  minWidth: "200px",
} as const

export function ClassBrowser() {
  const [filters, setFilters] = useState<PublicClassFilters>(DEFAULT_FILTERS)

  const { sessions, isLoading: sessionsLoading } = usePublicSessions()
  const { data, isLoading, isError, error } = usePublicClasses(filters)

  // Default to the most recent session once loaded
  useEffect(() => {
    if (sessions.length > 0 && !filters.session_id) {
      setFilters((prev) => ({ ...prev, session_id: sessions[0].id }))
    }
  }, [sessions, filters.session_id])

  // Apply frontend-only filters (available_only is client-side filtering)
  const filteredData = useMemo(() => {
    if (!data) return data
    if (!filters.available_only) return data
    const availableItems = data.items.filter(
      (item) => item.spots_available !== null && item.spots_available > 0,
    )
    return {
      ...data,
      items: availableItems,
      total: availableItems.length,
    }
  }, [data, filters.available_only])

  const handleFiltersChange = (newFilters: PublicClassFilters) => {
    // Reset offset when filters change (except when explicitly paginating)
    if (newFilters.offset === filters.offset && newFilters.offset !== 0) {
      const reset = { ...newFilters, offset: 0 }
      setFilters(reset)
      return
    }
    setFilters(newFilters)
  }

  const handleSessionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFilters((prev) => ({
      ...prev,
      session_id: event.target.value || undefined,
      offset: 0,
    }))
  }

  const handlePrevPage = () => {
    const limit = filters.limit || 50
    const offset = Math.max(0, (filters.offset || 0) - limit)
    setFilters((prev) => ({ ...prev, offset }))
  }

  const handleNextPage = () => {
    const limit = filters.limit || 50
    setFilters((prev) => ({ ...prev, offset: (prev.offset || 0) + limit }))
  }

  const currentPage = data ? data.page : 1
  const totalPages = data ? data.pages : 1

  return (
    <Container maxW="7xl" py={8}>
      {/* Header */}
      <VStack align="stretch" gap={2} mb={6}>
        <Heading size="2xl">Browse Swim Classes</Heading>
        <Text color="gray.600" fontSize="lg">
          Find the perfect swim class for your child. Filter by day, time, or
          skill level.
        </Text>
      </VStack>

      {/* Session Selector */}
      <Box mb={6}>
        <HStack gap={3} align="center">
          <Text fontWeight="medium" whiteSpace="nowrap">
            Session:
          </Text>
          {sessionsLoading ? (
            <Spinner size="sm" />
          ) : sessions.length > 0 ? (
            <select
              value={filters.session_id || ""}
              onChange={handleSessionChange}
              style={selectStyle}
            >
              <option value="">All Sessions</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ) : (
            <Text color="gray.500" fontSize="sm">
              No sessions available
            </Text>
          )}
        </HStack>
      </Box>

      {/* Main Layout: Filters Sidebar + Class Grid */}
      <Flex
        direction={{ base: "column", md: "row" }}
        gap={6}
        align="flex-start"
      >
        {/* Filters Sidebar */}
        <Box
          width={{ base: "100%", md: "280px" }}
          flexShrink={0}
          position={{ base: "static", md: "sticky" }}
          top={4}
        >
          <ClassFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
          />
        </Box>

        {/* Class Grid Area */}
        <Box flex={1} width="100%">
          {/* Loading State */}
          {isLoading && (
            <Flex justify="center" align="center" minH="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          )}

          {/* Error State */}
          {isError && (
            <Box
              bg="red.50"
              borderWidth="1px"
              borderColor="red.200"
              borderRadius="lg"
              p={6}
            >
              <Text color="red.600" fontWeight="medium">
                Error loading classes
              </Text>
              <Text color="red.500" fontSize="sm" mt={1}>
                {error instanceof Error
                  ? error.message
                  : "An unexpected error occurred. Please try again."}
              </Text>
            </Box>
          )}

          {/* Empty State */}
          {!isLoading && !isError && filteredData?.items.length === 0 && (
            <Box
              bg="gray.50"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="lg"
              p={8}
              textAlign="center"
            >
              <Text color="gray.600" fontSize="lg" fontWeight="medium">
                No classes found
              </Text>
              <Text color="gray.500" fontSize="sm" mt={2}>
                Try adjusting your filters to see more classes.
              </Text>
            </Box>
          )}

          {/* Success State: Class Count + Grid + Pagination */}
          {!isLoading &&
            !isError &&
            filteredData &&
            filteredData.items.length > 0 && (
              <VStack align="stretch" gap={4}>
                {/* Results Count */}
                <Text color="gray.600" fontSize="sm">
                  Showing {filteredData.items.length} of {filteredData.total}{" "}
                  class
                  {filteredData.total !== 1 ? "es" : ""}
                </Text>

                {/* Class Cards Grid */}
                <Grid
                  templateColumns={{
                    base: "1fr",
                    sm: "repeat(2, 1fr)",
                    lg: "repeat(3, 1fr)",
                  }}
                  gap={4}
                >
                  {filteredData.items.map((classInfo) => (
                    <ClassCard key={classInfo.id} classInfo={classInfo} />
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <HStack justify="center" gap={4} pt={4}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePrevPage}
                      disabled={currentPage <= 1}
                    >
                      Previous
                    </Button>
                    <Text fontSize="sm" color="gray.600">
                      Page {currentPage} of {totalPages}
                    </Text>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage >= totalPages}
                    >
                      Next
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
        </Box>
      </Flex>
    </Container>
  )
}
